import { Grid, Typography, Stack } from '@mui/material';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Link, useNavigate } from 'react-router-dom';

import { Highlight, PdfHighlighter, PdfLoader } from 'react-pdf-highlighter';

import 'react-pdf-highlighter/dist/style.css';

import { ReactComponent as ArrowDown } from 'common/assets/images/arrow-down.svg';
import { ReactComponent as ArrowUp } from 'common/assets/images/arrow-up.svg';
import { ReactComponent as Delete } from 'common/assets/images/delete-tip.svg';
import Banner from 'common/components/Banner/Banner';
import Breadcrumbs from 'common/components/Breadcrumbs/Breadcrumbs';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import {
	DATA_INGESTION_LABEL,
	PARSE_REGULATION_LABEL,
} from 'common/constants/DataIngestionConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { RootState } from 'common/store/AppStore.js';
import {
	setUploadParserSelectedTab,
	setParserHighlights,
	setHighlightButtonText,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import { urlWithTenant } from 'common/utils/utilFunctions';

import { uploadRegParser } from '../api';
import { findImmediatePreviousAndNext } from '../DataIngestionUtils';

import ParseRegulationTabInfo from './ParseRegulationTabInfo/ParseRegulationTabInfo';

import type {
	IHighlight,
	NewHighlight,
	ScaledPosition,
} from 'react-pdf-highlighter';

import styles from './UploadParser.module.scss';

const { dataIngestion } = rrcpDashboardRoutes;

const getNextId = () => {
	const crypto = window.crypto || window.msCrypto;
	const array = new Uint32Array(1);
	return String(crypto.getRandomValues(array)).slice(2);
};

const parseIdFromHash = () =>
	document.location.hash.slice('#highlight-'.length);

const resetHash = () => {
	document.location.hash = '';
};

const { OK } = HTTP_STATUS_CODES;

const UploadParser = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const uploadParserSelectedTab = useSelector(
		(state: RootState) => state.common.uploadParserSelectedTab,
	);

	const fileData = useSelector(
		(state: RootState) => state.dataingestion.parsedFileInfo,
	);

	const highlights = useSelector(
		(state: RootState) => state.common.parserHighlights,
	);

	const parserDocumentInfo = useSelector(
		(state: RootState) => state.common.parserDocumentInfo,
	);

	const highlightButtonText = useSelector(
		(state: RootState) => state.common.highlightButtonText,
	);

	const endHighlightId = useSelector(
		(state: RootState) => state.common.parserEndHighlightId,
	);

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const fileName = useSelector(
		(state: RootState) => state.common.processingFileName,
	);

	const dataType = useSelector(
		(state: RootState) => state.common.processingFileDataType,
	);

	const filePath = useSelector(
		(state: RootState) => state.common.processingFilePath,
	);

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const pdfViewerRef = useRef<any>(null);

	const [totalPages, setTotalPages] = useState<number>(1);

	const breadCrumbs = [
		<Link
			key="1"
			to={urlWithTenant(`/${dataIngestion}`)}
			data-testid="data-ingestion-test"
		>
			{DATA_INGESTION_LABEL}
		</Link>,
		<Typography key="2">{PARSE_REGULATION_LABEL}</Typography>,
	];

	const cancelButtonClicked = () => {
		navigate(urlWithTenant(`/${dataIngestion}`));
	};

	const backButtonClicked = () => {
		const newValue = uploadParserSelectedTab === 0 ? 1 : 0;
		dispatch(setUploadParserSelectedTab(newValue));
	};

	const parseButtonClicked = async () => {
		const filteredElements = highlights.filter(
			(res) => res.comment.text !== 'not allowed',
		);
		const highlightedTexts = filteredElements.map((res) => ({
			startPage: res.position.pageNumber || 1,
			startTitle: res.content.text ?? '',
			endPage:
				res.comment.emoji !== '' ? parseInt(res.comment.emoji, 2) : totalPages,
			endTitle: res.comment.text.length > 0 ? res.comment.text : '',
		}));
		const headers = {
			regulationMetadataRequest: parserDocumentInfo,
			fileMetadata: {
				fileName,
				filePath,
				dataType,
				recordCount: '1',
			},
			highlightedTexts,
			userEmail: loggedInUser?.email,
		};

		const response = await uploadRegParser(headers);
		if (response.statusCode === OK) {
			navigate(urlWithTenant(`/${dataIngestion}`));
		} else {
			dispatch(
				setToastMessage({
					message: 'Document failed to upload',
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const addHighlight = (highlight: NewHighlight) => {
		const newHighlights = [...highlights, { ...highlight, id: getNextId() }];
		dispatch(setParserHighlights(newHighlights));
	};

	const highlightButtonClicked = (highlight: NewHighlight) => {
		addHighlight(highlight);
	};

	const endHighlightButtonClicked = (highlight: NewHighlight) => {
		let updatedHighlights = [
			...highlights,
			{
				...highlight,
				id: endHighlightId.id,
				comment: {
					...highlight.comment,
					text: 'not allowed',
				},
			},
		];
		updatedHighlights = updatedHighlights.map((res) => {
			if (res.id === endHighlightId.id && res.comment.text !== 'not allowed') {
				return {
					...res,
					comment: {
						text: highlight.content.text ?? '',
						emoji: `${highlight.position.pageNumber}`,
					},
				};
			}
			return res;
		});
		dispatch(setParserHighlights(updatedHighlights));
		dispatch(setHighlightButtonText('Highlight'));
	};

	const scrollToHighlightFromHash = useCallback(() => {
		const highlight = getHighlightById(parseIdFromHash());
		if (highlight) {
			pdfViewerRef.current.scrollTo(highlight);
		}
	}, []);

	useEffect(() => {
		window.addEventListener('hashchange', scrollToHighlightFromHash, false);
		return () => {
			window.removeEventListener(
				'hashchange',
				scrollToHighlightFromHash,
				false,
			);
		};
	}, [scrollToHighlightFromHash]);

	const getHighlightById = (id: string) =>
		highlights.find((highlight) => highlight.id === id);

	const updateHash = (highlight: IHighlight) => {
		document.location.hash = `highlight-${highlight.id}`;
	};

	const getQuadrants = (position: ScaledPosition) => {
		const quadrants = highlights.map((res) => ({
			x1: res.position.boundingRect.x1,
			x2: res.position.boundingRect.x2,
			y1: res.position.boundingRect.y1,
			y2: res.position.boundingRect.y2,
		}));
		const newElement = {
			x1: position.boundingRect.x1,
			x2: position.boundingRect.x2,
			y1: position.boundingRect.y1,
			y2: position.boundingRect.y2,
		};
		return {
			quadrants,
			newElement,
		};
	};

	const downArrowClicked = (
		position: ScaledPosition,
		hideTipAndSelection: () => void,
	) => {
		const { quadrants, newElement } = getQuadrants(position);
		const { immediatePrevious } = findImmediatePreviousAndNext(
			quadrants,
			newElement,
		);
		const found = highlights.find(
			(res) =>
				res.position.boundingRect.x1 === immediatePrevious?.quadrant.x1 &&
				res.position.boundingRect.x2 === immediatePrevious?.quadrant.x2 &&
				res.position.boundingRect.y1 === immediatePrevious?.quadrant.y1 &&
				res.position.boundingRect.y2 === immediatePrevious?.quadrant.y2,
		);
		if (found) {
			hideTipAndSelection();
			updateHash(found);
			scrollToHighlightFromHash();
		}
	};

	const upArrowClicked = (
		position: ScaledPosition,
		hideTipAndSelection: () => void,
	) => {
		const { quadrants, newElement } = getQuadrants(position);
		const { immediateNext } = findImmediatePreviousAndNext(
			quadrants,
			newElement,
		);
		const found = highlights.find(
			(res) =>
				res.position.boundingRect.x1 === immediateNext?.quadrant.x1 &&
				res.position.boundingRect.x2 === immediateNext?.quadrant.x2 &&
				res.position.boundingRect.y1 === immediateNext?.quadrant.y1 &&
				res.position.boundingRect.y2 === immediateNext?.quadrant.y2,
		);
		if (found) {
			hideTipAndSelection();
			updateHash(found);
			scrollToHighlightFromHash();
		}
	};

	const tipComponent = (
		position: ScaledPosition,
		content: any,
		hideTipAndSelection: () => void,
	) => {
		const comment = { text: '', emoji: '' };
		return (
			<Grid
				className={
					highlightButtonText === 'Highlight'
						? styles['tip-view']
						: styles['tip-view-end-highlight']
				}
			>
				<ButtonComp
					btnType="secondary"
					clickHandler={() => {
						if (highlightButtonText === 'Highlight') {
							highlightButtonClicked({ content, position, comment });
						} else {
							endHighlightButtonClicked({ content, position, comment });
						}
						hideTipAndSelection();
					}}
					data-testid="submit-button"
				>
					{highlightButtonText}
				</ButtonComp>
				<Grid
					className={styles['tip-action-elements']}
					onClick={() => downArrowClicked(position, hideTipAndSelection)}
				>
					<ArrowDown />
				</Grid>
				<Grid
					className={styles['tip-action-elements']}
					onClick={() => upArrowClicked(position, hideTipAndSelection)}
				>
					<ArrowUp />
				</Grid>
				<Grid
					className={styles['tip-action-elements']}
					onClick={hideTipAndSelection}
				>
					<Delete />
				</Grid>
			</Grid>
		);
	};

	const highlightComponent = (highlight: any, isScrolledTo: boolean) => (
		<Highlight
			key={highlight.id}
			isScrolledTo={isScrolledTo}
			position={highlight.position}
			comment={highlight.comment}
		/>
	);

	const handleClose = useCallback(() => {
		dispatch(
			setToastMessage({
				isToastOpen: false,
				message: '',
				severity: '',
			}),
		);
	}, []);

	return (
		<Grid data-testid="upload-parser-container">
			<Breadcrumbs
				breadcrumbs={breadCrumbs}
				className={styles['upload-parser-bread-crumbs']}
				customClass
			/>
			<Banner>
				<Stack p={2}>
					<Typography className={styles['regulation-label']}>
						{PARSE_REGULATION_LABEL}
					</Typography>
				</Stack>
			</Banner>

			<Grid container xs={12} lg={12} mt={3} gap={2}>
				<Grid
					container
					display="flex"
					justifyContent="flex-start"
					alignItems="flex-start"
				>
					<Grid item xs={12} md={8}>
						<div
							style={{
								marginTop: '1.5rem',
								position: 'absolute',
								width: '60%',
								height: '100%',
							}}
						>
							<PdfLoader url={fileData} beforeLoad={<Loader show />}>
								{(pdfDocument) => {
									setTotalPages(pdfDocument.numPages);
									return (
										<PdfHighlighter
											ref={pdfViewerRef}
											pdfDocument={pdfDocument}
											pdfScaleValue="page-width"
											enableAreaSelection={(event) => event.altKey}
											onScrollChange={resetHash}
											scrollRef={(ref) => {
												pdfViewerRef.current.scrollTo = ref;
												scrollToHighlightFromHash();
											}}
											onSelectionFinished={(
												position,
												content,
												hideTipAndSelection,
											) => tipComponent(position, content, hideTipAndSelection)}
											highlightTransform={(
												highlight,
												index,
												setTip,
												hideTip,
												viewportToScaled,
												screenshot,
												isScrolledTo,
											) => highlightComponent(highlight, isScrolledTo)}
											highlights={highlights}
										/>
									);
								}}
							</PdfLoader>
						</div>
					</Grid>
					<Grid item xs={12} md={4}>
						<ParseRegulationTabInfo
							selectedTab={uploadParserSelectedTab}
							parserHighlights={highlights}
						/>
					</Grid>
				</Grid>
				<Grid className={styles['btn-footer-container']}>
					<Stack className={styles['btn-wrapper']}>
						<ButtonComp
							btnType="secondary"
							clickHandler={cancelButtonClicked}
							data-testid="discard-button"
						>
							Cancel
						</ButtonComp>
						<ButtonComp
							btnType="secondary"
							clickHandler={backButtonClicked}
							data-testid="submit-button"
						>
							{uploadParserSelectedTab === 0 ? 'Next' : 'Back'}
						</ButtonComp>
						<ButtonComp
							btnType="secondary"
							clickHandler={parseButtonClicked}
							disabled={
								highlights.length === 0 ||
								!Object.values(parserDocumentInfo).every((item) => item)
							}
							data-testid="submit-button"
						>
							Parse
						</ButtonComp>
					</Stack>
				</Grid>
			</Grid>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleClose}
				vertical="top"
				horizontal="right"
			/>
		</Grid>
	);
};

export default UploadParser;
